import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/context/LanguageContext";
import { listingPostLabels } from "@/i18n";
import { Badge } from "@/components/ui/badge";
import { ImageUploadButton } from "../ImageUploadButton";
import { ImageGrid } from "../ImageGrid";
import { SortableImageGrid } from "../SortableImageGrid";
import type {
  VehicleTestImages,
  ExteriorImage,
  DamageImage,
} from "@/types/vehicleTest";
import type { FileObject } from "@/types/shared/file";
import { photoSessionSourceProps } from "../../photoSession/photoSessionButtonProps";

interface SimpleImageSectionProps {
  title?: string;
  section: keyof VehicleTestImages;
  maxImages: number;
  tireType?: "primary" | "secondary";
  images: (ExteriorImage | DamageImage | FileObject)[];
  uploadingImages: Set<string>;
  onImageClick: (image: any, alt: string) => void; // Changed to pass full image object
  getThumbnailUrl?: (image: any) => string;
  onRemoveImage: (
    section: keyof VehicleTestImages,
    index: number,
    tireType?: "primary" | "secondary",
  ) => void;
  onRotate?: (image: any, direction: "left" | "right") => void;
  onReorder?: (newOrder: (ExteriorImage | DamageImage | FileObject)[]) => void;
  onFileSelect: (files: File[]) => Promise<void>;
  onSelect: () => void;
  onCameraSelect: () => void;
  rotatingImageId?: string | null;
  getImageRotation?: (imageId: string) => number;
}

export const SimpleImageSection: React.FC<SimpleImageSectionProps> = ({
  title,
  section,
  maxImages,
  tireType,
  images: imagesProp,
  uploadingImages,
  onImageClick,
  getThumbnailUrl,
  onRemoveImage,
  onReorder,
  onRotate,
  onFileSelect,
  onSelect,
  onCameraSelect,
  rotatingImageId,
  getImageRotation,
}) => {
  const safeSectionImages = imagesProp || [];
  const { language } = useLanguage();
  const imgLabels = listingPostLabels(language).images ?? {};
  const uploadLabel = imgLabels.uploadLabel ?? "Ladda upp bild(er)";
  const maxPerBatch = imgLabels.maxPerBatch5 ?? "(Max 5 åt gången)";
  const sourceProps = photoSessionSourceProps(imgLabels);

  const canAddMore = safeSectionImages.length < maxImages;
  const imagesToShow = safeSectionImages.length;

  const gridContent = onReorder ? (
    <SortableImageGrid
      images={safeSectionImages}
      section={section}
      onImageClick={onImageClick}
      getThumbnailUrl={getThumbnailUrl}
      onRemoveImage={(index) => onRemoveImage(section, index, tireType)}
      onReorder={onReorder}
      onRotate={onRotate}
      tireType={tireType}
      rotatingImageId={rotatingImageId}
      getImageRotation={getImageRotation}
    >
      {canAddMore && (
        <ImageUploadButton
          onSelect={onSelect}
          onFileSelect={onFileSelect}
          onCameraSelect={onCameraSelect}
          label={
            <>
              {uploadLabel} <span className="font-semibold">{maxPerBatch}</span>
            </>
          }
          isThumbnail={false}
          disabled={false}
          isLoading={uploadingImages.size > 0}
          section={section}
          tireType={tireType}
          {...sourceProps}
        />
      )}
    </SortableImageGrid>
  ) : (
    <ImageGrid
      images={safeSectionImages}
      section={section}
      onImageClick={onImageClick}
      getThumbnailUrl={getThumbnailUrl}
      onRemoveImage={(index) => onRemoveImage(section, index, tireType)}
      onRotate={onRotate}
      tireType={tireType}
      showIndex={safeSectionImages.length > 0}
    />
  );

  return (
    <Card className="border-0">
      {title && (
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {title}
            <Badge variant="outline">
              {imagesToShow}/{maxImages}
            </Badge>
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className="pt-4">
        {onReorder ? (
          gridContent
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {gridContent}

            {canAddMore && (
              <ImageUploadButton
                onSelect={onSelect}
                onFileSelect={onFileSelect}
                onCameraSelect={onCameraSelect}
                label={
                  <>
                    {uploadLabel}{" "}
                    <span className="font-semibold">{maxPerBatch}</span>
                  </>
                }
                isThumbnail={false}
                disabled={false}
                isLoading={uploadingImages.size > 0}
                section={section}
                tireType={tireType}
                {...sourceProps}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
